/**
 * A live emergency alert, across the top of every page, until it is ended.
 *
 * Shown to everyone the server says is in that building today — booked into or
 * checked in at a desk there, hosting a visitor there, or working there by
 * default — and to the people who raise and end alerts. The server decides
 * who; this only draws what it is given.
 *
 * Polls every 30 seconds while the tab is visible. The answer is almost
 * always an empty list, which is a few bytes.
 *
 * Employee rebuild C10: each live alert the person is one of the recipients
 * of carries a RESPOND button, which opens "Are you safe?" (AreYouSafeModal).
 * The first time an unanswered alert appears in this tab the modal opens by
 * itself — full attention, as the prototype draws it — and once closed it is
 * not forced open again for that alert; the button stays. Wardens and
 * emergency:trigger holders also get Roll call.
 *
 * FM Manager rebuild B23E: a SITE INCIDENT (`incident` — lift entrapment,
 * medical, power failure, flooding) reaches only the site team, and asks
 * nobody to move: no Respond, no Roll call, never the "Are you safe?" modal.
 * It shows its message and "The site team has been told".
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { apiGet } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { hasPermFor } from '@/utils/permissions';
import { type RespondableAlert } from './AreYouSafeModal';
import EvacuationOverlay from '@/components/Tech/EvacuationOverlay';

interface ActiveAlert extends RespondableAlert {
  id: string;
  alert_type: string;
  type_label: string;
  severity: string;
  message: string;
  location?: string | null;
  building_name?: string | null;
}

/** Alerts whose modal has already opened by itself in this tab. Module state,
 *  not storage: a reload is a new look at an emergency and may ask again. */
const autoOpened = new Set<string>();

/** 🔴 Employee R2 (E2E F1): at 360 px the message was `min-w-0 flex-1` in a
 *  wrapping row, so it shrank to a ~45 px sliver beside the title and wrapped
 *  one word per line (banner 236 px tall). `basis-full` gives it its own full
 *  line on a phone; from sm up it is the flexible middle column again. */
export const BANNER_MESSAGE_CLASS = 'min-w-0 basis-full sm:basis-0 sm:flex-1';

/** The CSS variable carrying the live banner's height, read by the toaster. */
export const BANNER_HEIGHT_VAR = '--emergency-banner-h';

export default function EmergencyBanner() {
  const user = useAuthStore((st) => st.user);
  const canManage = hasPermFor(user, 'emergency:trigger');
  const q = useQuery<ActiveAlert[]>({
    queryKey: ['emergency', 'active'],
    queryFn: async () => (await apiGet<ActiveAlert[]>('/emergency/alerts/active')) ?? [],
    refetchInterval: 30_000,
    enabled: Boolean(user),
    // A failed poll must not blank a banner that was showing: keep the last
    // answer rather than hide an evacuation because one request dropped.
    placeholderData: (prev) => prev,
  });
  const alerts = q.data ?? [];
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = alerts.find((a) => a.id === openId) ?? null;

  useEffect(() => {
    const first = alerts.find((a) => a.alert_type !== 'incident' && a.can_respond && !a.my_response
      && !autoOpened.has(a.id));
    if (first && openId === null) {
      autoOpened.add(first.id);
      setOpenId(first.id);
    }
  }, [alerts, openId]);

  // 🔴 THE TOASTS SAT ON TOP OF IT. Toasts are pinned top-right, and the
  // "Welcome back!" one covered the right-hand end of a live evacuation banner
  // for its first seconds — the alert's own words. The banner publishes its
  // height and the toaster (main.tsx) starts below it; 0 when nothing is live,
  // so every other moment is exactly as before (Employee B1/B13).
  const ref = useRef<HTMLDivElement>(null);
  const live = alerts.length > 0;
  useLayoutEffect(() => {
    const root = document.documentElement;
    const h = live ? (ref.current?.offsetHeight ?? 0) : 0;
    root.style.setProperty(BANNER_HEIGHT_VAR, `${h}px`);
    return () => { root.style.setProperty(BANNER_HEIGHT_VAR, '0px'); };
  });

  if (!live) return null;

  return (
    <div ref={ref} role="alert" aria-live="assertive" data-testid="emergency-banner">
      {alerts.map((a) => a.alert_type === 'incident' ? (
        <div key={a.id} data-testid="banner-incident"
             className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-warning-700 px-4 py-2 text-sm text-white">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          <strong className="uppercase tracking-wide">{a.type_label}</strong>
          <span className="font-medium">{a.building_name ?? ''}</span>
          <span className={BANNER_MESSAGE_CLASS} data-testid="banner-message">
            {a.message}{a.location ? ` — ${a.location}` : ''}
            <span className="block text-xs text-white/85" data-testid="banner-incident-told">The site team has been told.</span>
          </span>
          {canManage && (
            <Link to="/safety" className="rounded bg-white/15 px-2 py-0.5 text-xs font-semibold hover:bg-white/25">
              Manage
            </Link>
          )}
        </div>
      ) : (
        <div key={a.id}
             className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm text-white ${
               a.severity === 'info' ? 'bg-brand-700' : 'bg-danger-700'}`}>
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          <strong className="uppercase tracking-wide">{a.type_label}</strong>
          <span className="font-medium">{a.building_name ?? ''}</span>
          <span className={BANNER_MESSAGE_CLASS} data-testid="banner-message">{a.message}{a.location ? ` — ${a.location}` : ''}</span>
          {a.can_respond && (
            <button type="button" onClick={() => setOpenId(a.id)} data-testid="banner-respond"
                    className="rounded bg-white px-2.5 py-0.5 text-xs font-semibold text-danger-700 hover:bg-white/90">
              {a.my_response ? (a.my_response === 'safe' ? "Answered: I'm safe" : 'Answered: I need help') : 'Respond'}
            </button>
          )}
          {a.can_view_roll_call && (
            <Link to={`/safety/roll-call/${a.id}`} data-testid="banner-roll-call"
                  className="rounded bg-white/15 px-2 py-0.5 text-xs font-semibold hover:bg-white/25">
              Roll call
            </Link>
          )}
          {canManage && (
            <Link to="/safety" className="rounded bg-white/15 px-2 py-0.5 text-xs font-semibold hover:bg-white/25">
              Manage
            </Link>
          )}
        </div>
      ))}
      {/* FM Technician C20: the answer is the full-screen evacuation overlay,
          for every person an alert reaches (checked-in technicians included). */}
      <EvacuationOverlay alert={opened} onClose={() => setOpenId(null)} />
    </div>
  );
}
